/**
 * Adapter: ADK session history → TranscriptTurnItem[].
 *
 * The canonical session source-of-truth is the platform-agents
 * `/v1/agents/{agentId}/sessions/{sessionId}/history` endpoint, which returns
 * one entry per ADK `Event` rather than per audit event.  This avoids the
 * history-replay duplication bug present in the old audit-events trace
 * collapser (the same user prompt appeared once per subsequent
 * `agent.model.request` because each request carries the full accumulated
 * `contents` array).
 *
 * Wire format (one event per dict in `events`):
 *   {
 *     id?: string,
 *     timestamp?: string (ISO 8601),
 *     role: 'user' | 'model' | string,
 *     parts: [
 *       { text?: string, thought?: boolean,
 *         function_call?: { name, id, args },
 *         function_response?: { name, id, response },
 *         inline_data?: { mime_type, data } }
 *     ]
 *   }
 *
 * Render mapping (matching the audit-event collapser's output shape so the
 * existing InspectorTranscriptTurn renderer keeps working unchanged):
 *
 *   ADK event role=user, parts contain text
 *     → TranscriptTurnItem { role: 'user', content: text }
 *
 *   ADK event role=user, parts contain function_response(s)
 *     → for each response: TranscriptTurnItem
 *       { role: 'tool', toolName: name,
 *         content: { parameters: <paired call args>, result: response } }
 *
 *   ADK event role=model, parts contain text only
 *     → TranscriptTurnItem { role: 'assistant', content: text }
 *
 *   ADK event role=model, parts contain function_call(s) only
 *     → SKIPPED — the renderer null-renders pure function_call announcements,
 *       and the tool turn (emitted later from the function_response) carries
 *       the same call args.  Skipping reduces noise in the turn array.
 *
 *   ADK event role=model, parts contain text AND function_call(s)
 *     → TranscriptTurnItem { role: 'assistant',
 *         content: { text, function_calls: [...] } }
 *
 * Thought parts (`{ thought: true, text: '...' }`) represent the model's
 * internal chain-of-thought reasoning emitted alongside the normal output.
 * They are NOT mixed into the assistant text (which would conflate reasoning
 * with the user-facing response); instead their joined text is surfaced on
 * the resulting turn's optional ``thinkingText`` field so transcript
 * renderers can display it as a clearly de-emphasised block above the
 * response.
 *
 * Note: A2A subagent delegation does NOT surface in ADK events as a distinct
 * `a2a_call` turn — from ADK's perspective it's an ordinary `function_call`
 * to an AgentTool.  The subagent's own session is recoverable via the
 * sessions list with `includeSubagents=true`.  The old `a2a_call` turn type
 * is no longer emitted by this adapter.
 */

import type { TranscriptImage, TranscriptTurnItem } from '../types'

// ── Wire types (mirroring the /history response shape) ──────────────────────

export interface AdkFunctionCall {
  name: string
  id?: string
  args?: Record<string, unknown>
}

export interface AdkFunctionResponse {
  name: string
  id?: string
  response: unknown
}

export interface AdkInlineData {
  mime_type: string
  data: string
}

export interface AdkPart {
  text?: string
  thought?: boolean
  function_call?: AdkFunctionCall
  function_response?: AdkFunctionResponse
  inline_data?: AdkInlineData
}

/**
 * Subset of ADK ``EventActions`` fields surfaced by the backend.  Currently
 * only ``transfer_to_agent`` is serialised — it's set by the ADK runner on
 * the model event that called the ``transfer_to_agent`` built-in tool and
 * names the sub-agent control was handed to.
 */
export interface AdkEventActions {
  transfer_to_agent?: string | null
}

export interface AdkEvent {
  id?: string
  timestamp?: string
  role: 'user' | 'model' | string
  parts: AdkPart[]
  /**
   * ADK ``Event.author`` — ``"user"`` or the name of the agent (e.g.
   * ``"support_manager"``).  Optional for backward compatibility with older
   * backend payloads that didn't serialise it.
   */
  author?: string
  /**
   * ADK ``Event.actions`` (subset).  Present on model events that performed
   * a side-effecting action such as ``transfer_to_agent``.  Optional for the
   * same backward-compatibility reason.
   */
  actions?: AdkEventActions
}

export interface SessionHistoryResponse {
  agent_id?: string
  session_id?: string
  events: AdkEvent[]
  state?: Record<string, unknown>
}

// ── Adapter ─────────────────────────────────────────────────────────────────

/**
 * Convert an ADK session-history events array into the TranscriptTurnItem[]
 * shape consumed by the Stream transcript renderer.
 *
 * @param events - ADK events ordered chronologically (as returned by the
 *   `/v1/agents/{agentId}/sessions/{sessionId}/history` endpoint).
 * @param fallbackTimestamp - ISO 8601 timestamp to use when an event has no
 *   `timestamp` field of its own (defensive — newer backends always emit
 *   timestamps).  Typically the session's `startedAt`.
 */
/**
 * Per-call origin metadata captured during the first pass so a tool turn
 * (emitted later from the paired ``function_response``) can carry attribution
 * back to the agent that made the call.
 *
 * - ``args`` — original arguments the agent passed to the tool.
 * - ``author`` — ADK ``Event.author`` of the model event that emitted the
 *   ``function_call`` (e.g. ``"support_manager"``).  Falls back to undefined
 *   when an older backend didn't serialise authors.
 * - ``transferredToAgent`` — ``Event.actions.transfer_to_agent`` set on the
 *   originating model event.  Set only for calls to the ADK built-in
 *   ``transfer_to_agent`` tool; used by the renderer to attach a handoff
 *   banner to the corresponding tool turn.
 */
interface CallOrigin {
  args: Record<string, unknown>
  author?: string
  transferredToAgent?: string
}

/**
 * ADK-internal tool names that are pure plumbing and must NOT render as
 * transcript rows.
 *
 * `adk_request_confirmation` is the wrapper ADK injects around any tool that
 * triggers `before_tool_callback` and requires operator approval. Both the
 * wrapper's function_call AND the operator's function_response to it carry no
 * semantic value to the operator: the gating action is already represented by
 * the original tool's row, and the operator's decision is captured in the
 * resulting function_response on the original tool (see "rejection-feedback
 * synthesis" in services/agents/backend/internal/adapters/runtime/rejection_feedback.py).
 *
 * Filtering these out collapses the transcript from
 *   send_email(call) → send_email(stub-response) → adk_request_confirmation(call)
 *   → adk_request_confirmation(response) → send_email(rejection-response)
 * down to a single send_email row that shows the args and the final outcome
 * — matching what the agent actually observes in its own context.
 */
const ADK_PLUMBING_TOOL_NAMES = new Set<string>(['adk_request_confirmation'])

/**
 * Signature of the "tool gated, waiting for operator" stub response that ADK
 * synthesizes when a tool's `before_tool_callback` suspends execution. The
 * exact strings are duplicated in the backend
 * (services/agents/backend/internal/adapters/runtime/retry_plugin.py) and the
 * legacy Flutter admin portal — keep this list in sync if those change.
 *
 * We only treat a function_response as a stub when its content matches one of
 * these signatures AND a later non-stub response exists for the same call id;
 * see the dedup logic below.
 */
const GATING_STUB_SIGNATURES = [
  'This tool call requires confirmation, please approve or reject.',
  'This tool call is rejected.',
]

function isGatingStubResponse(response: unknown): boolean {
  if (response == null) return false
  if (typeof response === 'string') {
    return GATING_STUB_SIGNATURES.some((sig) => response.includes(sig))
  }
  if (typeof response === 'object') {
    const obj = response as Record<string, unknown>
    const err = obj.error
    if (typeof err === 'string') {
      return GATING_STUB_SIGNATURES.some((sig) => err.includes(sig))
    }
  }
  return false
}

export function historyToTranscriptTurns(
  events: AdkEvent[],
  fallbackTimestamp: string,
): TranscriptTurnItem[] {
  // First pass: index function_call origin by id so we can pair them with
  // the function_response that arrives in a subsequent user event.  ADK
  // guarantees unique ids per call; if a backend emits the call without an
  // id we fall back to matching by tool name (last-call-wins within the
  // session).  We capture more than just args here — the originating model
  // event's ``author`` and ``actions.transfer_to_agent`` flow through to
  // the tool turn so the renderer can attribute the call to the right
  // agent and surface handoff markers.
  //
  // adk_request_confirmation calls are skipped at this stage so their args
  // never leak into a paired tool row even if a stray function_response with
  // the same id were to slip through the second-pass filter.
  const callOriginById = new Map<string, CallOrigin>()
  const callOriginByName = new Map<string, CallOrigin>()

  for (const event of events) {
    if (event.role !== 'model') continue
    const eventAuthor = event.author
    const eventTransfer = event.actions?.transfer_to_agent ?? undefined
    for (const part of event.parts ?? []) {
      const fc = part.function_call
      if (!fc) continue
      if (ADK_PLUMBING_TOOL_NAMES.has(fc.name)) continue
      const origin: CallOrigin = {
        args: fc.args ?? {},
        author: eventAuthor,
        // Only attach the transfer signal to the call that actually performed
        // the handoff (i.e. the ``transfer_to_agent`` call itself).  Without
        // this guard, every tool call in a model event that also transferred
        // would inherit the flag.  In practice ADK emits the transfer call
        // alone, but the guard keeps the contract precise.
        transferredToAgent:
          eventTransfer && fc.name === 'transfer_to_agent' ? eventTransfer : undefined,
      }
      if (fc.id) callOriginById.set(fc.id, origin)
      if (fc.name) callOriginByName.set(fc.name, origin)
    }
  }

  // Second pass: build per-call-id deduplication maps.
  //
  // A single tool invocation (one function_call.id) can produce multiple
  // function_response events when it is gated:
  //   1. A synthesized "requires confirmation" stub written by ADK at
  //      suspend time so the agent loop has something to consume.
  //   2. The post-resolution response written by the agents-service
  //      rejection-feedback callback (or the actual tool output on approve).
  //
  // The agent's own context, by the time it resumes, contains only the LAST
  // response for that call id — the earlier stub is overwritten/superseded.
  // Showing the stub in the transcript misleads the operator into thinking
  // the same tool fired twice. We dedupe by keeping the last response
  // for each call id (tracked here as `lastResponseEventIdxById`), and emit
  // a single tool row at that event's position.
  //
  // Calls/responses for ADK_PLUMBING_TOOL_NAMES (e.g. adk_request_confirmation)
  // are excluded outright — they have no operator-facing meaning.
  const lastResponseEventIdxById = new Map<string, number>()
  events.forEach((event, eventIdx) => {
    if (event.role !== 'user') return
    for (const part of event.parts ?? []) {
      const fr = part.function_response
      if (!fr) continue
      if (ADK_PLUMBING_TOOL_NAMES.has(fr.name)) continue
      if (!fr.id) continue
      lastResponseEventIdxById.set(fr.id, eventIdx)
    }
  })

  const turns: TranscriptTurnItem[] = []
  let turnIndex = 0

  events.forEach((event, eventIdx) => {
    const ts = event.timestamp ?? fallbackTimestamp
    const parts = event.parts ?? []
    const eventAuthor = event.author

    // Categorise parts.  Thoughts are kept separate from the assistant's
    // user-facing text — they surface on the turn's ``thinkingText`` field
    // for the renderer to display in a de-emphasised block above the
    // response, instead of being concatenated into the response itself.
    // ADK plumbing tool names are dropped at this stage so they never
    // produce calls or responses in the transcript.
    const textParts: string[] = []
    const thoughtParts: string[] = []
    const functionCalls: AdkFunctionCall[] = []
    const functionResponses: AdkFunctionResponse[] = []
    const imageParts: TranscriptImage[] = []

    for (const part of parts) {
      if (part.thought) {
        if (typeof part.text === 'string' && part.text.length > 0) {
          thoughtParts.push(part.text)
        }
        continue
      }
      if (typeof part.text === 'string' && part.text.length > 0) {
        textParts.push(part.text)
      }
      if (part.function_call) {
        if (ADK_PLUMBING_TOOL_NAMES.has(part.function_call.name)) continue
        functionCalls.push(part.function_call)
      }
      if (part.function_response) {
        if (ADK_PLUMBING_TOOL_NAMES.has(part.function_response.name)) continue
        functionResponses.push(part.function_response)
      }
      // Inline images handed to / emitted by the agent. Only `image/*` mime
      // types are surfaced inline; other binary blobs (audio, pdf, ...) are
      // left to the existing artifact/tool surfaces and ignored here.
      const inline = part.inline_data
      if (
        inline &&
        typeof inline.data === 'string' &&
        inline.data.length > 0 &&
        typeof inline.mime_type === 'string' &&
        inline.mime_type.startsWith('image/')
      ) {
        imageParts.push({ mimeType: inline.mime_type, data: inline.data })
      }
    }

    const text = textParts.join('\n\n')
    const thinkingText = thoughtParts.length > 0 ? thoughtParts.join('\n\n') : undefined
    const images = imageParts.length > 0 ? imageParts : undefined

    if (event.role === 'user') {
      // Emit a user turn when there's text OR inline images. A screenshot
      // handed to the agent often arrives as an image-only user event (no
      // accompanying text); previously that event was dropped entirely.
      if (text.length > 0 || images) {
        turns.push({
          turnIndex: turnIndex++,
          role: 'user',
          content: text,
          timestamp: ts,
          // User events always have author === 'user' in ADK; default to that
          // when the backend didn't include the field (older payloads).
          author: eventAuthor ?? 'user',
          images,
        })
      }

      // Tool turns: one per function_response, paired with the originating
      // call's args + author so the renderer can show parameters + result
      // together and attribute the call to the agent that made it.  The
      // function_response itself lives on a synthetic ``user`` event in ADK
      // (with author='user'), so we deliberately propagate the *originating*
      // model event's author instead — that is the agent that should appear
      // in the transcript's active-agent header at this point in time.
      //
      // Dedup: when a call.id has multiple function_responses (the gating
      // stub + the resolution response), skip the earlier ones and emit a
      // single row at the position of the final response. When the id is
      // missing we fall back to filtering only obvious gating stubs so the
      // duplicate doesn't render. See the second-pass comment above.
      for (const fr of functionResponses) {
        if (fr.id) {
          const lastIdx = lastResponseEventIdxById.get(fr.id)
          if (lastIdx !== undefined && lastIdx !== eventIdx) {
            // This is an earlier response for a call id that has a later
            // response; the later one will render in its place.
            continue
          }
        } else if (isGatingStubResponse(fr.response)) {
          // No id to dedup by — best effort: drop the stub regardless. If
          // the resolution response is absent too the operator just sees no
          // row, which matches "tool is gated and still pending" — and the
          // pending state is already surfaced via the approval card panel.
          continue
        }

        const origin =
          (fr.id ? callOriginById.get(fr.id) : undefined) ??
          callOriginByName.get(fr.name)
        const args = origin?.args ?? {}
        turns.push({
          turnIndex: turnIndex++,
          role: 'tool',
          toolName: fr.name,
          content: {
            parameters: args,
            result: fr.response,
          },
          timestamp: ts,
          author: origin?.author,
          transferredToAgent: origin?.transferredToAgent,
        })
      }
      return
    }

    if (event.role === 'model') {
      const transferredToAgent = event.actions?.transfer_to_agent ?? undefined
      if (text.length > 0 && functionCalls.length > 0) {
        // Mixed event: emit a single assistant turn carrying both the text
        // and a note of the function_calls.  The renderer filters calls
        // without inline results, so the calls themselves render as a
        // separate tool turn (driven by the later function_response, or by
        // the pending-tool emission below when no response has arrived).
        turns.push({
          turnIndex: turnIndex++,
          role: 'assistant',
          content: {
            text,
            function_calls: functionCalls.map((fc) => ({
              name: fc.name,
              args: fc.args ?? {},
            })),
          },
          timestamp: ts,
          author: eventAuthor,
          // Rare in practice (a transfer call typically has no text), but
          // surface it if it does happen so the renderer can still draw the
          // banner relative to the assistant turn.
          transferredToAgent,
          thinkingText,
          images,
        })
      } else if (text.length > 0) {
        turns.push({
          turnIndex: turnIndex++,
          role: 'assistant',
          content: text,
          timestamp: ts,
          author: eventAuthor,
          transferredToAgent,
          thinkingText,
          images,
        })
      } else if (thinkingText) {
        // Thoughts-only event (no response text yet, no function_call) —
        // emit an assistant turn carrying just the thinking so the operator
        // can see the model's reasoning.  Otherwise we'd silently drop the
        // event and lose context that's already on the wire.
        turns.push({
          turnIndex: turnIndex++,
          role: 'assistant',
          content: '',
          timestamp: ts,
          author: eventAuthor,
          transferredToAgent,
          thinkingText,
          images,
        })
      } else if (images) {
        // Image-only model event (no text, no thinking, no calls) — surface
        // the image so it isn't silently dropped.
        turns.push({
          turnIndex: turnIndex++,
          role: 'assistant',
          content: '',
          timestamp: ts,
          author: eventAuthor,
          transferredToAgent,
          images,
        })
      }

      // Pending-tool turns for in-flight sessions.
      //
      // Historically we skipped pure function_call events outright — the
      // reasoning was that the matching function_response would arrive
      // later and emit the tool turn there, with the call's args attached
      // via the callOrigin map.  That holds for completed sessions, but
      // breaks for sessions viewed mid-flight: the response hasn't been
      // written yet (e.g. the agent is awaiting a slow API, a sub-agent
      // call, or a gated tool whose stub response hasn't been synthesised),
      // so the function_call renders nothing and the operator sees an
      // empty transcript even though the agent is busy.  See AgentSessionModal
      // empty-state UX for the wider context.
      //
      // Fix: for every function_call in this event that has NO matching
      // function_response anywhere in the events list, emit a tool turn at
      // the call's position with no ``result``.  ``parseTurnContent`` maps
      // ``result === undefined`` to ``status='running'``, giving the
      // operator a visible "tool in flight" card.  When the response later
      // arrives (in a subsequent /history fetch), the regular tool-turn
      // emission at the response event takes over — the id-based gate
      // below prevents emitting both a pending turn and a completed turn
      // for the same call id within a single render pass.
      //
      // Function calls without an id are conservatively skipped: we can't
      // reliably dedup them against a future response (name-based pairing
      // would over-match when the agent calls the same tool twice), so
      // they preserve the historical "skip pure function_call" behaviour
      // rather than risk duplicate turns.  Modern ADK always sets ids on
      // function_call parts, so this only affects legacy payloads.
      for (const fc of functionCalls) {
        if (!fc.id) continue
        if (lastResponseEventIdxById.has(fc.id)) continue
        turns.push({
          turnIndex: turnIndex++,
          role: 'tool',
          toolName: fc.name,
          content: {
            parameters: fc.args ?? {},
            // No ``result`` — parseTurnContent treats this as
            // ``status='running'``, which is exactly the signal we want:
            // the call has been issued, the response hasn't arrived.
          },
          timestamp: ts,
          author: eventAuthor,
          // Only attach the transfer signal to the actual transfer_to_agent
          // call, matching the existing convention in the callOrigin pass.
          transferredToAgent:
            transferredToAgent && fc.name === 'transfer_to_agent'
              ? transferredToAgent
              : undefined,
        })
      }
      return
    }

    // Any other role (e.g. 'system' if it ever appears) — emit as assistant
    // text if there's anything to show.  This is defensive; ADK only emits
    // 'user' and 'model' today.
    if (text.length > 0) {
      turns.push({
        turnIndex: turnIndex++,
        role: 'assistant',
        content: text,
        timestamp: ts,
        author: eventAuthor,
      })
    }
  })

  return turns
}
